'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef } from 'react';
import { MentionPicker, useMentionPicker, } from '../mention-picker/index.js';
/**
 * Connects Tiptap's suggestion plugin to `useMentionPicker` and
 * `MentionPicker` from @rookcom/monarch-react/collaboration: debounced,
 * abortable search, the highlighted option and the APG listbox keys. Focus
 * stays in the editor, which points at the highlighted option with
 * aria-activedescendant.
 */
export function useMentionBridge(options, messages) {
    const trigger = options?.trigger ?? '@';
    const command = useRef(null);
    // Escape closes the list until this suggestion ends (the caret leaves it).
    const dismissedAt = useRef(null);
    const picker = useMentionPicker({
        search: (text, ctx) => (options ? options.search(text, ctx) : []),
        onSelect: (item) => command.current?.(item),
        debounceMs: options?.debounceMs ?? 150,
    });
    const live = useRef(picker);
    live.current = picker;
    const suggestion = useMemo(() => {
        const sync = (p) => {
            command.current = (item) => p.command({ id: item.id, label: item.label });
            if (dismissedAt.current === p.range.from)
                return;
            const r = p.clientRect?.();
            const query = {
                trigger,
                text: p.query,
                start: p.range.from,
                end: p.range.to,
            };
            live.current.update(query, r ? { top: r.top, left: r.left, height: r.height } : null);
        };
        return {
            allowSpaces: options?.allowSpaces ?? false,
            items: () => [],
            render: () => ({
                onStart: sync,
                onUpdate: sync,
                onKeyDown: ({ event, range }) => {
                    const used = live.current.handleKeyDown(event);
                    if (used && event.key === 'Escape')
                        dismissedAt.current = range.from;
                    return used;
                },
                onExit: () => {
                    dismissedAt.current = null;
                    command.current = null;
                    live.current.close();
                },
            }),
        };
        // The suggestion config is read once, when the editor is created.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (!options)
        return undefined;
    const fieldAttributes = Object.fromEntries(Object.entries(picker.fieldProps).filter((e) => typeof e[1] === 'string'));
    return {
        suggestion,
        isOpen: () => live.current.query !== null,
        fieldAttributes: picker.query !== null ? fieldAttributes : {},
        list: (_jsx(MentionPicker, { ...picker.pickerProps, messages: {
                label: messages.suggestions,
                loading: messages.suggestionsLoading,
                empty: messages.suggestionsEmpty,
                error: messages.suggestionsError,
            } })),
    };
}
//# sourceMappingURL=rich-text-editor.mentions.js.map